import { Check, Download, Eye, Pencil, RefreshCw, Save, Trash2, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import useSWR from "swr";
import type { CheckoutColorMode } from "@/shared/contracts";
import { apiFetch, jsonBody, swrFetcher } from "@/web/api";
import { PageHeader } from "@/web/components/page-header";
import { Button } from "@/web/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/web/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/web/components/ui/dialog";
import { Input } from "@/web/components/ui/input";
import { Label } from "@/web/components/ui/label";
import { Loading } from "@/web/components/ui/loading";
import { Textarea } from "@/web/components/ui/textarea";

interface ThemeCopyField { key: string; label: string; default: string }

interface ThemeItem {
  id: string;
  name: string;
  version: string;
  author: string;
  description: string;
  enabled: boolean;
  copy_spec: ThemeCopyField[];
  created_at: string;
}

interface SettingsResponse {
  checkout_color_mode: CheckoutColorMode;
  checkout_custom_css: string;
}

const COLOR_MODES: Array<{ id: CheckoutColorMode; label: string; hint: string }> = [
  { id: "auto", label: "自动", hint: "跟随访客设备的深色偏好" },
  { id: "light", label: "白天", hint: "始终使用浅色" },
  { id: "dark", label: "黑夜", hint: "始终使用深色" },
];

/** 预览可切换的订单状态：不切这个就看不到「支付成功」长什么样。 */
const PREVIEW_STATES: Array<{ id: "pending" | "paid" | "ended"; label: string }> = [
  { id: "pending", label: "待支付" },
  { id: "paid", label: "支付成功" },
  { id: "ended", label: "已结束" },
];

/** 预览可强制的配色：用于同时比对深/浅两套样式。 */
const PREVIEW_SCHEMES: Array<{ id: "auto" | "light" | "dark"; label: string }> = [
  { id: "auto", label: "跟随系统" },
  { id: "light", label: "浅色" },
  { id: "dark", label: "深色" },
];

export function AppearancePage() {
  const { data, isLoading, mutate } = useSWR<SettingsResponse>("/admin-api/settings", swrFetcher);
  const { data: themeData, mutate: mutateThemes } = useSWR<{ themes: ThemeItem[]; builtin_theme_id?: string }>("/admin-api/themes", swrFetcher);

  const [colorMode, setColorMode] = useState<CheckoutColorMode>("auto");
  const [customCss, setCustomCss] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [previewKey, setPreviewKey] = useState(0);
  const [previewState, setPreviewState] = useState<"pending" | "paid" | "ended">("pending");
  const [previewScheme, setPreviewScheme] = useState<"auto" | "light" | "dark">("auto");
  const [renameTarget, setRenameTarget] = useState<ThemeItem | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<ThemeItem | null>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!data) return;
    setColorMode(data.checkout_color_mode);
    setCustomCss(data.checkout_custom_css ?? "");
  }, [data]);

  const themes = themeData?.themes ?? [];
  const activeTheme = themes.find((item) => item.enabled) ?? null;
  // 内置「系统默认」样式：不在主题列表里，但同样支持预览与下载
  const builtinId = themeData?.builtin_theme_id ?? "__builtin_default__";
  const previewName = previewId === null
    ? ""
    : previewId === builtinId
      ? "系统默认"
      : (themes.find((item) => item.id === previewId)?.name ?? "");

  async function saveAppearance() {
    setSaving(true);
    try {
      await apiFetch("/admin-api/settings", {
        method: "PUT",
        ...jsonBody({ checkout_color_mode: colorMode, checkout_custom_css: customCss }),
      });
      toast.success("已保存，收银台立即生效");
      await mutate();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "保存失败");
    } finally {
      setSaving(false);
    }
  }

  async function uploadZip(file: File) {
    if (!file.name.toLowerCase().endsWith(".zip")) {
      toast.error("请上传 .zip 格式的主题包");
      return;
    }
    setUploading(true);
    try {
      const buffer = await file.arrayBuffer();
      const response = await apiFetch<{ theme: ThemeItem }>("/admin-api/themes", {
        method: "POST",
        headers: { "content-type": "application/zip", "x-csrf-token": readCsrf() },
        body: buffer,
      });
      toast.success(`主题「${response.theme.name}」已上传`);
      await mutateThemes();
      setPreviewId(response.theme.id);
      setPreviewKey((k) => k + 1);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "上传失败");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  function readCsrf() {
    const match = document.cookie.match(/(?:^|; )alimpay_csrf=([^;]*)/);
    return match ? decodeURIComponent(match[1]) : "";
  }

  async function toggleTheme(theme: ThemeItem) {
    setBusy(true);
    try {
      const action = theme.enabled ? "disable" : "enable";
      await apiFetch(`/admin-api/themes/${theme.id}/${action}`, { method: "POST" });
      toast.success(theme.enabled ? "已禁用，回到默认样式" : `已启用「${theme.name}」`);
      await mutateThemes();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "操作失败");
    } finally {
      setBusy(false);
    }
  }

  async function rename() {
    if (!renameTarget) return;
    setBusy(true);
    try {
      await apiFetch(`/admin-api/themes/${renameTarget.id}/rename`, { method: "POST", ...jsonBody({ name: renameValue }) });
      toast.success("已重命名");
      setRenameTarget(null);
      await mutateThemes();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "重命名失败");
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await apiFetch(`/admin-api/themes/${deleteTarget.id}`, { method: "DELETE" });
      toast.success("主题已删除");
      if (previewId === deleteTarget.id) setPreviewId(null);
      setDeleteTarget(null);
      await mutateThemes();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "删除失败");
    } finally {
      setBusy(false);
    }
  }

  /** 下载主题包：内置「系统默认」也走同一个接口，拿到的是可直接改、再上传的 zip。 */
  async function downloadTheme(id: string, name: string) {
    setBusy(true);
    try {
      const response = await fetch(`/admin-api/themes/${encodeURIComponent(id)}/download`, { credentials: "same-origin" });
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as { message?: string } | null;
        throw new Error(body?.message ?? `下载失败（HTTP ${response.status}）`);
      }
      const blob = await response.blob();
      // 文件名以响应头为准（主题名可能是中文，后端用 filename*=UTF-8'' 传）
      const header = response.headers.get("content-disposition") ?? "";
      const matched = /filename\*=UTF-8''([^;]+)/i.exec(header);
      const filename = matched ? decodeURIComponent(matched[1]) : `${name}.zip`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success(`已下载「${name}」`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "下载失败");
    } finally {
      setBusy(false);
    }
  }

  if (isLoading) return <Loading label="正在读取外观配置" />;

  return (
    <>
      <PageHeader
        title="收银台外观"
        description="上传主题包即可整体改变收银台样式；同一时刻只有一个主题生效。"
        actions={<Button onClick={saveAppearance} disabled={saving}><Save />{saving ? "正在保存…" : "保存配色"}</Button>}
      />

      <div className="space-y-5">
        <Card>
          <CardHeader>
            <CardTitle>主题包</CardTitle>
            <CardDescription>
              上传 zip 即可生效。包内需包含 theme.json 与 checkout.html，
              且不能包含 &lt;script&gt;。
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div
              onClick={() => fileRef.current?.click()}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();
                const file = event.dataTransfer.files?.[0];
                if (file) void uploadZip(file);
              }}
              className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed px-4 py-8 text-center transition-colors hover:border-primary/50 hover:bg-foreground/[0.02]"
            >
              <Upload className="size-5 text-muted" />
              <div className="text-sm font-medium">{uploading ? "正在上传…" : "点击或拖入主题包（.zip）"}</div>
              <div className="text-xs text-muted">单个文件不超过 5MB</div>
              <input
                ref={fileRef}
                type="file"
                accept=".zip,application/zip"
                className="hidden"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file) void uploadZip(file);
                }}
              />
            </div>

            <div className={`flex flex-wrap items-center gap-3 rounded-lg border px-4 py-3 ${activeTheme ? "" : "border-primary/40 bg-primary/[0.03]"}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-medium">系统默认</span>
                  {activeTheme ? null : (
                    <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">使用中</span>
                  )}
                </div>
                <div className="mt-0.5 truncate text-xs text-muted">内置样式 · 支持配色模式与自定义 CSS</div>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <Button variant="ghost" size="sm" onClick={() => { setPreviewId(builtinId); setPreviewKey((k) => k + 1); }}><Eye />预览</Button>
                <Button variant="ghost" size="sm" disabled={busy} onClick={() => void downloadTheme(builtinId, "系统默认")}><Download />下载</Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy || !activeTheme}
                  onClick={() => {
                    if (activeTheme) void toggleTheme(activeTheme);
                  }}
                >
                  {activeTheme ? "启用" : "使用中"}
                </Button>
              </div>
            </div>

            {themes.length === 0 ? (
              <p className="rounded-md bg-foreground/[0.035] px-4 py-3 text-xs leading-5 text-muted">
                还没有上传主题包。当前收银台使用上面的「系统默认」样式，上传后点「启用」即可切换。
              </p>
            ) : (
              <div className="space-y-2">
                {themes.map((theme) => (
                  <div key={theme.id} className="flex flex-wrap items-center gap-3 rounded-lg border px-4 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="truncate text-sm font-medium">{theme.name}</span>
                        {theme.enabled ? (
                          <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary">使用中</span>
                        ) : null}
                      </div>
                      <div className="mt-0.5 truncate text-xs text-muted">
                        {[theme.version, theme.author, theme.description].filter(Boolean).join(" · ") || "无描述"}
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      <Button variant="ghost" size="sm" onClick={() => { setPreviewId(theme.id); setPreviewKey((k) => k + 1); }}><Eye />预览</Button>
                      <Button variant="ghost" size="sm" disabled={busy} onClick={() => void downloadTheme(theme.id, theme.name)}><Download />下载</Button>
                      <Button variant={theme.enabled ? "outline" : "outline"} size="sm" disabled={busy} onClick={() => void toggleTheme(theme)}>
                        {theme.enabled ? "禁用" : "启用"}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => { setRenameTarget(theme); setRenameValue(theme.name); }}><Pencil />重命名</Button>
                      <Button variant="ghost" size="sm" className="text-destructive hover:bg-destructive/10" onClick={() => setDeleteTarget(theme)}><Trash2 />删除</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {previewId ? (
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <CardTitle>预览 · {previewName}</CardTitle>
                  <CardDescription>固定示例数据（¥10.30 / 示例商品）。切换下方状态与配色可分别验收「支付成功样式」和深浅两套。主题未启用时，预览不影响线上收银台。</CardDescription>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setPreviewKey((k) => k + 1)}><RefreshCw />刷新</Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-medium text-muted">状态</span>
                  {PREVIEW_STATES.map((item) => (
                    <Button
                      key={item.id}
                      type="button"
                      size="sm"
                      variant={previewState === item.id ? "default" : "outline"}
                      onClick={() => setPreviewState(item.id)}
                    >
                      {item.label}
                    </Button>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs font-medium text-muted">配色</span>
                  {PREVIEW_SCHEMES.map((item) => (
                    <Button
                      key={item.id}
                      type="button"
                      size="sm"
                      variant={previewScheme === item.id ? "default" : "outline"}
                      onClick={() => setPreviewScheme(item.id)}
                    >
                      {item.label}
                    </Button>
                  ))}
                </div>
              </div>
              <iframe
                key={`${previewKey}-${previewState}-${previewScheme}`}
                title="主题预览"
                src={`/admin-api/themes/${previewId}/preview?state=${previewState}&scheme=${previewScheme}`}
                className="h-[620px] w-full rounded-lg border bg-background"
              />
            </CardContent>
          </Card>
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle>配色模式</CardTitle>
            <CardDescription>全局生效，系统默认样式与主题包都适用。选「自动」时跟随访客设备的深色偏好。</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="max-w-xs space-y-2">
              <Label htmlFor="color-mode">模式</Label>
              <select
                id="color-mode"
                value={colorMode}
                onChange={(event) => setColorMode(event.target.value as CheckoutColorMode)}
                className="h-9 w-full rounded-md border bg-background px-3 text-sm shadow-sm outline-none focus-visible:border-primary"
              >
                {COLOR_MODES.map((item) => (<option key={item.id} value={item.id}>{item.label}</option>))}
              </select>
              <p className="text-xs leading-5 text-muted">{COLOR_MODES.find((item) => item.id === colorMode)?.hint}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>高级：自定义 CSS</CardTitle>
            <CardDescription>对系统默认样式与主题包都生效（会覆盖主题自带样式，写在主题 CSS 之后）。</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Textarea
              value={customCss}
              onChange={(event) => setCustomCss(event.target.value)}
              spellCheck={false}
              placeholder={".checkout-scope { \\n  --primary: #7c3aed;\\n}"}
              className="min-h-40 font-mono text-xs leading-6"
            />
            <p className="text-xs leading-5 text-muted">
              可用变量：<code className="font-mono">--background</code>、
              <code className="font-mono">--foreground</code>、
              <code className="font-mono">--primary</code>。不允许包含 style/script 标签。
            </p>
          </CardContent>
        </Card>
      </div>

      <Dialog open={renameTarget !== null} onOpenChange={(open) => { if (!open && !busy) setRenameTarget(null); }}>
        <DialogContent>
          <DialogTitle>重命名主题</DialogTitle>
          <DialogDescription>只改显示名称，不影响主题内容。</DialogDescription>
          <div className="mt-4 space-y-2">
            <Label htmlFor="theme-name">名称</Label>
            <Input id="theme-name" value={renameValue} onChange={(event) => setRenameValue(event.target.value)} maxLength={40} />
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="ghost" disabled={busy} onClick={() => setRenameTarget(null)}>取消</Button>
            <Button disabled={busy || renameValue.trim().length === 0} onClick={rename}><Check />保存</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={deleteTarget !== null} onOpenChange={(open) => { if (!open && !busy) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogTitle>删除主题</DialogTitle>
          <DialogDescription>将删除主题「{deleteTarget?.name}」及其文件，此操作不可恢复。</DialogDescription>
          {deleteTarget?.enabled ? (
            <p className="mt-3 rounded-lg bg-destructive/[0.06] px-3.5 py-3 text-xs leading-5 text-destructive">
              该主题正在使用中，删除后收银台会回到系统默认样式。
            </p>
          ) : null}
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="ghost" disabled={busy} onClick={() => setDeleteTarget(null)}>取消</Button>
            <Button variant="danger" disabled={busy} onClick={remove}><Trash2 />确认删除</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
